import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { EspionLogo, EspionMark } from "./EspionLogo";
// ── Email Helper ──────────────────────────────────
async function sendEmail(type, to, data) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ type, to, data }),
      }
    );
  } catch (e) {
    console.warn('Email send failed:', e);
  }
}

// Get designer email by profile ID
async function getDesignerEmail(projectId) {
  // Step 1: get designer_id from project
  const { data: project } = await supabase
    .from('projects')
    .select('designer_id')
    .eq('id', projectId)
    .single();
  if (!project?.designer_id) return null;
  // Step 2: get profile
  const { data: profile } = await supabase
    .from('profiles')
    .select('email, full_name')
    .eq('id', project.designer_id)
    .single();
  return profile || null;
}



/* ─────────────────────────────────────────────
   ESPION ADMIN — Internal Management Console
   Espion Furniture Co. · St. Louis, MO
   ───────────────────────────────────────────── */

const T = {
  bg:"#08080a", bg2:"#0f0f12", bg3:"#161619", bg4:"#1e1e22",
  gold:"#c4a265", goldLight:"#d9bc85", goldDark:"#a68945",
  cream:"#f2ece0", text:"#d8d4cc", textMuted:"#7a766e", textDim:"#4e4a44",
  border:"#222228", danger:"#b84040", success:"#4a8a5a", blue:"#5878a0",
  shadow:"rgba(0,0,0,0.6)", overlay:"rgba(0,0,0,0.85)",
};

const font = {
  display:"'Cormorant Garamond','Garamond',Georgia,serif",
  body:"'DM Sans','Helvetica Neue',sans-serif",
  mono:"'JetBrains Mono',monospace",
};

const css = `
  *{margin:0;padding:0;box-sizing:border-box}
  body{background:${T.bg};color:${T.text};font-family:'DM Sans','Helvetica Neue',sans-serif}
  ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:${T.bg}}
  ::-webkit-scrollbar-thumb{background:${T.border};border-radius:3px}
  input,textarea,select{font-family:'DM Sans','Helvetica Neue',sans-serif;color-scheme:dark}
  button{font-family:'DM Sans','Helvetica Neue',sans-serif}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
  .fade{animation:fadeUp .35s cubic-bezier(.23,1,.32,1) both}
`;

// ── Icons ──
const I = ({name,size=17,color="currentColor"}) => {
  const d = {
    home:<><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/></>,
    users:<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>,
    folder:<><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></>,
    message:<><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></>,
    dollar:<><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></>,
    book:<><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></>,
    plus:<><path d="M12 5v14"/><path d="M5 12h14"/></>,
    edit:<><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    trash:<><path d="M3 6h18"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></>,
    check:<><path d="M20 6L9 17l-5-5"/></>,
    x:<><path d="M18 6L6 18"/><path d="M6 6l12 12"/></>,
    send:<><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></>,
    upload:<><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></>,
    alert:<><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    chart:<><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></>,
    logout:<><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
    shield:<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    arrowL:<><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></>,
    file:<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
    eye:<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    settings:<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{d[name]}</svg>;
};

// ── UI Components ──
const Card = ({children, style:s, onClick}) => (
  <div onClick={onClick} style={{background:T.bg2,border:`1px solid ${T.border}`,borderRadius:4,padding:24,...s,cursor:onClick?"pointer":"default",transition:"border-color .2s"}} onMouseEnter={e=>{if(onClick)e.currentTarget.style.borderColor=T.gold+"40"}} onMouseLeave={e=>{if(onClick)e.currentTarget.style.borderColor=T.border}}>{children}</div>
);

const Btn = ({children,primary,small,danger,ghost,icon,onClick,disabled,style:s}) => (
  <button onClick={onClick} disabled={disabled} style={{display:"inline-flex",alignItems:"center",gap:7,padding:small?"7px 14px":"10px 20px",fontSize:small?10:11,fontWeight:600,letterSpacing:".08em",textTransform:"uppercase",color:primary?T.bg:danger?T.danger:ghost?T.textMuted:T.gold,cursor:disabled?"not-allowed":"pointer",background:primary?`linear-gradient(135deg,${T.gold},${T.goldDark})`:danger?`${T.danger}12`:"transparent",border:primary?"none":danger?`1px solid ${T.danger}30`:ghost?"none":`1px solid ${T.gold}35`,borderRadius:2,opacity:disabled?.4:1,transition:"all .2s",...s}}>{icon&&<I name={icon} size={small?13:15}/>}{children}</button>
);

const Badge = ({children,color}) => (
  <span style={{display:"inline-flex",alignItems:"center",padding:"3px 10px",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:color||T.gold,borderRadius:2,background:`${color||T.gold}15`,border:`1px solid ${color||T.gold}25`}}>{children}</span>
);

const Field = ({label,value,onChange,placeholder,textarea,type="text",select,options,style:s}) => (
  <div style={{marginBottom:16,...s}}>
    {label&&<label style={{display:"block",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:T.textMuted,marginBottom:7}}>{label}</label>}
    {select
      ?<select value={value} onChange={e=>onChange(e.target.value)} style={{width:"100%",padding:"10px 14px",background:T.bg3,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,outline:"none"}}>
        {options.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
      </select>
      :textarea
      ?<textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={4} style={{width:"100%",padding:"10px 14px",background:T.bg3,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,resize:"vertical",outline:"none",lineHeight:1.65}}/>
      :<input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{width:"100%",padding:"10px 14px",background:T.bg3,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,outline:"none"}}/>
    }
  </div>
);

const Modal = ({children,onClose,maxWidth=600}) => (
  <div style={{position:"fixed",inset:0,background:T.overlay,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:24,backdropFilter:"blur(8px)"}} onClick={onClose}>
    <div onClick={e=>e.stopPropagation()} style={{background:T.bg2,border:`1px solid ${T.border}`,borderRadius:6,maxWidth,width:"100%",padding:36,maxHeight:"90vh",overflow:"auto",boxShadow:`0 24px 80px ${T.shadow}`}}>{children}</div>
  </div>
);

const Divider = () => <div style={{height:1,background:T.border,margin:"20px 0"}}/>;

const Avatar = ({initials,size=32}) => (
  <div style={{width:size,height:size,borderRadius:"50%",background:`linear-gradient(135deg,${T.gold},${T.goldDark})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*.35,fontWeight:700,color:T.bg,flexShrink:0}}>{initials}</div>
);

// ── Status options ──
const PROJECT_STATUSES = ["Quoting","Design Review","In Production","Completed"];
const PROJECT_STAGES = ["Pending Quote","Awaiting Approval","Material Selection","Fabrication","Finishing","Delivery","Delivered"];
const PRIORITIES = ["Normal","High","Urgent"];
const INVOICE_STATUSES = ["Outstanding","Paid","Upcoming","Cancelled"];

// ═══════════════════════════════════════
// AUTH
// ═══════════════════════════════════════
function AdminAuth({onAuth}) {
  const[email,setEmail]=useState("");
  const[password,setPassword]=useState("");
  const[error,setError]=useState("");
  const[loading,setLoading]=useState(false);

  const login = async () => {
    if(!email||!password){setError("Please enter your credentials.");return;}
    setLoading(true);setError("");
    const{data,error:e}=await supabase.auth.signInWithPassword({email,password});
    if(e){setError("Invalid credentials. Admin access only.");setLoading(false);return;}
    onAuth(data.user);
    setLoading(false);
  };

  return(
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{width:400,padding:48,background:T.bg2,border:`1px solid ${T.border}`,borderRadius:6,boxShadow:`0 24px 80px ${T.shadow}`}}>
        <div style={{marginBottom:36,textAlign:"center"}}>
          <EspionMark size={72} style={{margin:"0 auto"}}/>
          <div style={{fontSize:9,color:"#a09070",letterSpacing:".2em",textTransform:"uppercase",fontFamily:font.body,fontWeight:600,marginTop:14}}>Admin Console</div>
        </div>
        <Field label="Email" value={email} onChange={setEmail} placeholder="admin@espionfurniture.com" type="email"/>
        <Field label="Password" value={password} onChange={setPassword} placeholder="••••••••••" type="password"/>
        {error&&<div style={{fontSize:12,color:T.danger,marginBottom:16,padding:"10px 14px",background:`${T.danger}10`,borderRadius:3}}>{error}</div>}
        <Btn primary onClick={login} disabled={loading} style={{width:"100%",justifyContent:"center",padding:"12px",fontSize:12}}>
          {loading?"Signing in...":"Sign In to Admin"}
        </Btn>
        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:24,fontSize:11,color:T.textDim,justifyContent:"center"}}>
          <I name="shield" size={13} color={T.textDim}/>
          <span>Restricted to Espion team members only</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════
function AdminDashboard({stats,onNavigate}) {
  return(
    <div>
      <div style={{marginBottom:32}}>
        <h2 style={{fontFamily:font.display,fontSize:36,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Overview</h2>
        <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>Espion Designer Portal — Admin Console</p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:32}}>
        {[
          {label:"Total Designers",value:stats.designers,icon:"users",color:T.gold,nav:"designers"},
          {label:"Active Projects",value:stats.activeProjects,icon:"folder",color:T.blue,nav:"projects"},
          {label:"Pending Approvals",value:stats.pendingApprovals,icon:"alert",color:T.danger,nav:"projects"},
          {label:"Outstanding Invoices",value:stats.outstandingInvoices,icon:"dollar",color:T.success,nav:"invoices"},
        ].map((s,i)=>(
          <Card key={i} onClick={()=>onNavigate(s.nav)} style={{borderTop:`2px solid ${s.color}25`,cursor:"pointer"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"start"}}>
              <div>
                <div style={{fontFamily:font.display,fontSize:40,fontWeight:600,color:T.cream,fontStyle:"italic",lineHeight:1}}>{s.value}</div>
                <div style={{fontSize:10,color:T.textMuted,letterSpacing:".08em",textTransform:"uppercase",marginTop:8}}>{s.label}</div>
              </div>
              <I name={s.icon} size={20} color={s.color}/>
            </div>
          </Card>
        ))}
      </div>

      {stats.pendingApprovals>0&&(
        <div style={{background:`${T.danger}08`,border:`1px solid ${T.danger}25`,borderRadius:4,padding:"16px 20px",marginBottom:24,display:"flex",alignItems:"center",gap:12}}>
          <I name="alert" size={18} color={T.danger}/>
          <span style={{fontSize:13,color:T.text}}><strong style={{color:T.cream}}>{stats.pendingApprovals} approval{stats.pendingApprovals>1?"s":""}</strong> waiting for designer action.</span>
          <Btn small primary onClick={()=>onNavigate("projects")} style={{marginLeft:"auto"}}>View Projects</Btn>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
        <Card style={{padding:0,overflow:"hidden"}}>
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`}}>
            <h3 style={{fontFamily:font.display,fontSize:17,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Recent Projects</h3>
          </div>
          {stats.recentProjects?.map((p,i)=>(
            <div key={p.id} style={{padding:"14px 24px",borderBottom:i<stats.recentProjects.length-1?`1px solid ${T.border}`:"none",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:T.cream,marginBottom:3}}>{p.name}</div>
                <div style={{fontSize:11,color:T.textMuted}}>{p.profiles?.full_name||"Unknown"} · {p.profiles?.firm||""}</div>
              </div>
              <Badge color={p.status==="In Production"?T.gold:p.status==="Completed"?T.success:p.status==="Design Review"?T.blue:T.textMuted}>{p.status}</Badge>
            </div>
          ))}
        </Card>

        <Card style={{padding:0,overflow:"hidden"}}>
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`}}>
            <h3 style={{fontFamily:font.display,fontSize:17,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Designer Accounts</h3>
          </div>
          {stats.recentDesigners?.map((d,i)=>(
            <div key={d.id} style={{padding:"12px 24px",borderBottom:i<stats.recentDesigners.length-1?`1px solid ${T.border}`:"none",display:"flex",alignItems:"center",gap:12}}>
              <Avatar initials={(d.full_name||d.email||"?").charAt(0).toUpperCase()} size={32}/>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:T.cream}}>{d.full_name||d.email}</div>
                <div style={{fontSize:11,color:T.textMuted}}>{d.firm||"—"}</div>
              </div>
              <Badge color={T.success}>{d.tier||"Partner"}</Badge>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// DESIGNERS
// ═══════════════════════════════════════
function Designers() {
  const[designers,setDesigners]=useState([]);
  const[loading,setLoading]=useState(true);
  const[search,setSearch]=useState("");
  const[sel,setSel]=useState(null);
  const[showInvite,setShowInvite]=useState(false);
  const[inviteEmail,setInviteEmail]=useState("");
  const[inviteName,setInviteName]=useState("");
  const[inviteFirm,setInviteFirm]=useState("");
  const[inviteSending,setInviteSending]=useState(false);
  const[inviteMsg,setInviteMsg]=useState("");

  useEffect(()=>{
    supabase.from("profiles").select("*").order("created_at",{ascending:false})
      .then(({data})=>{setDesigners(data||[]);setLoading(false);});
  },[]);

  const filtered=designers.filter(d=>
    (d.full_name||"").toLowerCase().includes(search.toLowerCase())||
    (d.firm||"").toLowerCase().includes(search.toLowerCase())||
    (d.email||"").toLowerCase().includes(search.toLowerCase())
  );

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28}}>
        <div>
          <h2 style={{fontFamily:font.display,fontSize:32,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Designer Accounts</h2>
          <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>{designers.length} registered designers</p>
        </div>
        <Btn primary icon="plus" onClick={()=>{setShowInvite(true);setInviteMsg("");}}>Invite Designer</Btn>
      </div>

      <div style={{marginBottom:20}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, firm, or email..." style={{width:"100%",maxWidth:400,padding:"10px 14px",background:T.bg3,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,outline:"none"}}/>
      </div>

      <Card style={{padding:0,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"2fr 2fr 1fr 1fr 100px",padding:"10px 24px",borderBottom:`1px solid ${T.border}`,fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:T.textDim}}>
          <span>Designer</span><span>Firm</span><span>Tier</span><span>Joined</span><span></span>
        </div>
        {loading&&<div style={{padding:32,textAlign:"center",color:T.textMuted}}>Loading...</div>}
        {filtered.map((d,i)=>(
          <div key={d.id} style={{display:"grid",gridTemplateColumns:"2fr 2fr 1fr 1fr 100px",padding:"16px 24px",borderBottom:i<filtered.length-1?`1px solid ${T.border}`:"none",alignItems:"center",transition:"background .2s"}} onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <Avatar initials={(d.full_name||d.email||"?").charAt(0).toUpperCase()} size={32}/>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:T.cream}}>{d.full_name||"—"}</div>
                <div style={{fontSize:11,color:T.textDim}}>{d.email}</div>
              </div>
            </div>
            <div style={{fontSize:13,color:T.text}}>{d.firm||"—"}</div>
            <Badge color={T.success}>{d.tier||"Partner"}</Badge>
            <div style={{fontSize:11,color:T.textDim}}>{d.member_since?.split("T")[0]||d.created_at?.split("T")[0]||"—"}</div>
            <Btn small icon="eye" onClick={()=>setSel(d)}>View</Btn>
          </div>
        ))}
        {!loading&&filtered.length===0&&<div style={{padding:32,textAlign:"center",color:T.textMuted}}>No designers found.</div>}
      </Card>

      {showInvite&&(
        <Modal onClose={()=>{setShowInvite(false);setInviteEmail("");setInviteName("");setInviteFirm("");setInviteMsg("");}} maxWidth={520}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <h2 style={{fontFamily:font.display,fontSize:22,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Invite Designer</h2>
            <span onClick={()=>setShowInvite(false)} style={{cursor:"pointer",color:T.textMuted}}><I name="x" size={18}/></span>
          </div>
          <p style={{fontSize:13,color:T.textMuted,marginBottom:20,lineHeight:1.6}}>Send an invitation email to a designer. They'll receive a link to create their account and access the client portal.</p>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:T.textMuted,marginBottom:7}}>Email Address *</label>
            <input type="email" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="designer@theirfirm.com" style={{width:"100%",padding:"10px 14px",background:T.bg3,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,outline:"none"}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:4}}>
            <div>
              <label style={{display:"block",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:T.textMuted,marginBottom:7}}>Full Name</label>
              <input value={inviteName} onChange={e=>setInviteName(e.target.value)} placeholder="Jane Smith" style={{width:"100%",padding:"10px 14px",background:T.bg3,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,outline:"none"}}/>
            </div>
            <div>
              <label style={{display:"block",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:T.textMuted,marginBottom:7}}>Design Firm</label>
              <input value={inviteFirm} onChange={e=>setInviteFirm(e.target.value)} placeholder="Smith Interiors" style={{width:"100%",padding:"10px 14px",background:T.bg3,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,outline:"none"}}/>
            </div>
          </div>
          {inviteMsg&&<div style={{fontSize:12,color:inviteMsg.includes("Error")?T.danger:T.success,margin:"12px 0",padding:"10px 14px",background:`${inviteMsg.includes("Error")?T.danger:T.success}10`,borderRadius:3}}>{inviteMsg}</div>}
          <div style={{display:"flex",gap:10,marginTop:16}}>
            <Btn primary icon="send" disabled={!inviteEmail.trim()||inviteSending} onClick={async()=>{
              if(!inviteEmail.includes("@")){setInviteMsg("Error: Please enter a valid email address.");return;}
              setInviteSending(true);
              setInviteMsg("");
              try{
                await fetch(
                  `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-email`,
                  {method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({type:"invite",to:inviteEmail,data:{name:inviteName,firm:inviteFirm}})}
                );
              }catch(e){}
              setInviteMsg(`✓ Invitation sent to ${inviteEmail}`);
              setInviteSending(false);
            }}>{inviteSending?"Sending...":"Send Invitation"}</Btn>
            <Btn ghost onClick={()=>setShowInvite(false)}>Cancel</Btn>
          </div>
          <div style={{marginTop:16,padding:"12px 14px",background:T.bg3,borderRadius:3,fontSize:11,color:T.textDim,lineHeight:1.6}}>
            💡 <strong style={{color:T.textMuted}}>Tip:</strong> You can also share the signup link directly: <span style={{color:T.gold,fontFamily:font.mono}}>client.espionfurniture.com</span>
          </div>
        </Modal>
      )}

      {sel&&(
        <Modal onClose={()=>setSel(null)}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",gap:14}}>
              <Avatar initials={(sel.full_name||sel.email||"?").charAt(0).toUpperCase()} size={48}/>
              <div>
                <h2 style={{fontFamily:font.display,fontSize:24,fontWeight:600,color:T.cream,fontStyle:"italic"}}>{sel.full_name||sel.email}</h2>
                <Badge color={T.success}>{sel.tier||"Partner"}</Badge>
              </div>
            </div>
            <span onClick={()=>setSel(null)} style={{cursor:"pointer",color:T.textMuted}}><I name="x" size={18}/></span>
          </div>
          <Divider/>
          {[
            {l:"Email",v:sel.email},
            {l:"Firm",v:sel.firm||"—"},
            {l:"Phone",v:sel.phone||"—"},
            {l:"Role",v:sel.role||"designer"},
            {l:"Member Since",v:sel.member_since?.split("T")[0]||"—"},
          ].map(r=>(
            <div key={r.l} style={{display:"flex",gap:12,marginBottom:14}}>
              <span style={{fontSize:11,color:T.textDim,textTransform:"uppercase",letterSpacing:".08em",width:100,flexShrink:0,paddingTop:1}}>{r.l}</span>
              <span style={{fontSize:13,color:T.text}}>{r.v}</span>
            </div>
          ))}
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// PROJECTS
// ═══════════════════════════════════════
function Projects() {
  const[projects,setProjects]=useState([]);
  const[designers,setDesigners]=useState([]);
  const[loading,setLoading]=useState(true);
  const[sel,setSel]=useState(null);
  const[showNew,setShowNew]=useState(false);
  const[showMessage,setShowMessage]=useState(null);
  const[showApproval,setShowApproval]=useState(null);
  const[showUpdate,setShowUpdate]=useState(null);
  const[filterStatus,setFilterStatus]=useState("All");

  const fetchProjects = async () => {
    const{data}=await supabase.from("projects").select(`
      *,
      profiles(id,full_name,firm,email),
      project_updates(*),
      approvals(*),
      project_files(*)
    `).order("created_at",{ascending:false});
    setProjects(data||[]);
    setLoading(false);
  };

  useEffect(()=>{
    fetchProjects();
    supabase.from("profiles").select("id,full_name,firm,email").then(({data})=>setDesigners(data||[]));
  },[]);

  const filtered=filterStatus==="All"?projects:projects.filter(p=>p.status===filterStatus);
  const pendingApprovals=projects.flatMap(p=>(p.approvals||[]).filter(a=>a.status==="pending"));

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28}}>
        <div>
          <h2 style={{fontFamily:font.display,fontSize:32,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Projects</h2>
          <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>{projects.length} total · {pendingApprovals.length} pending approvals</p>
        </div>
        <Btn primary icon="plus" onClick={()=>setShowNew(true)}>New Project</Btn>
      </div>

      {pendingApprovals.length>0&&(
        <div style={{background:`${T.danger}08`,border:`1px solid ${T.danger}25`,borderRadius:4,padding:"14px 20px",marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
          <I name="alert" size={16} color={T.danger}/>
          <span style={{fontSize:13,color:T.text}}>{pendingApprovals.length} designer approval{pendingApprovals.length>1?"s":""} pending response.</span>
        </div>
      )}

      <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
        {["All",...PROJECT_STATUSES].map(s=>(
          <span key={s} onClick={()=>setFilterStatus(s)} style={{padding:"6px 14px",fontSize:11,fontWeight:500,cursor:"pointer",borderRadius:2,background:filterStatus===s?T.gold:"transparent",color:filterStatus===s?T.bg:T.textMuted,border:`1px solid ${filterStatus===s?T.gold:T.border}`,transition:"all .2s"}}>{s}</span>
        ))}
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {loading&&<Card><div style={{textAlign:"center",color:T.textMuted,padding:20}}>Loading...</div></Card>}
        {filtered.map(p=>(
          <Card key={p.id} style={{padding:0,overflow:"hidden"}}>
            <div style={{padding:"18px 24px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:12}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                    <span style={{fontSize:20}}>{p.thumbnail||"🪑"}</span>
                    <h3 style={{fontFamily:font.display,fontSize:18,fontWeight:600,color:T.cream,fontStyle:"italic"}}>{p.name}</h3>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                    <Badge color={p.status==="In Production"?T.gold:p.status==="Completed"?T.success:p.status==="Design Review"?T.blue:T.textMuted}>{p.status}</Badge>
                    <Badge color={p.priority==="Urgent"?T.danger:p.priority==="High"?T.gold:T.textMuted}>{p.priority}</Badge>
                    <span style={{fontSize:11,color:T.textMuted}}>{p.profiles?.full_name||"Unknown"} · {p.profiles?.firm||""}</span>
                    {(p.approvals||[]).filter(a=>a.status==="pending").length>0&&(
                      <Badge color={T.danger}>Awaiting Designer Approval</Badge>
                    )}
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontFamily:font.display,fontSize:22,fontWeight:600,color:T.gold,fontStyle:"italic"}}>{p.budget||"TBD"}</div>
                  <div style={{fontSize:11,color:T.textDim,marginTop:2}}>{p.progress||0}% complete</div>
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <Btn small icon="eye" onClick={()=>setSel(p)}>Detail</Btn>
                <Btn small icon="message" onClick={()=>setShowMessage(p)}>Send Message</Btn>
                <Btn small icon="alert" onClick={()=>setShowApproval(p)}>Request Approval</Btn>
                <Btn small icon="edit" onClick={()=>setShowUpdate(p)}>Add Update</Btn>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {showNew&&<NewProjectModal designers={designers} onClose={()=>setShowNew(false)} onSave={async(data)=>{await supabase.from("projects").insert(data);await fetchProjects();setShowNew(false);}}/>}
      {sel&&<ProjectDetailModal project={sel} onClose={()=>setSel(null)} onRefresh={fetchProjects}/>}
      {showMessage&&<SendMessageModal project={showMessage} onClose={()=>setShowMessage(null)} onSend={async(text)=>{const r=await supabase.from("messages").insert({project_id:showMessage.id,sender_name:"Espion Team",sender_initial:"ET",is_espion:true,text});
              if(!r.error){
                const profile=await getDesignerEmail(showMessage.id);
                if(profile?.email){
                  await sendEmail("new_message",profile.email,{
                    projectName:showMessage.name,
                    messageText:text,
                    senderName:"Espion Team"
                  });
                }
              }
              setShowMessage(null);}}/>}
      {showApproval&&<RequestApprovalModal project={showApproval} onClose={()=>setShowApproval(null)} onSave={async(data)=>{const r=await supabase.from("approvals").insert({...data,project_id:showApproval.id,status:"pending"});
              if(!r.error){
                const profile=await getDesignerEmail(showApproval.id);
                if(profile?.email){
                  await sendEmail("approval_request",profile.email,{
                    projectName:showApproval.name,
                    approvalType:data.type,
                    description:data.description,
                    options:data.options
                  });
                }
              }
              await fetchProjects();setShowApproval(null);}}/>}
      {showUpdate&&<AddUpdateModal project={showUpdate} onClose={()=>setShowUpdate(null)} onSave={async(text,progress)=>{await supabase.from("project_updates").insert({project_id:showUpdate.id,author:"Espion Team",text});
              if(progress!==null)await supabase.from("projects").update({progress}).eq("id",showUpdate.id);
              const profile=await getDesignerEmail(showUpdate.id);
              if(profile?.email){
                await sendEmail("project_update",profile.email,{
                  projectName:showUpdate.name,
                  updateText:text,
                  progress:progress!==null?parseInt(progress):showUpdate.progress
                });
              }
              await fetchProjects();setShowUpdate(null);}}/>}
    </div>
  );
}

function NewProjectModal({designers,onClose,onSave}) {
  const[name,setName]=useState("");
  const[designerId,setDesignerId]=useState(designers[0]?.id||"");
  useEffect(()=>{if(!designerId&&designers.length>0)setDesignerId(designers[0].id);},[designers]);
  const[status,setStatus]=useState("Quoting");
  const[stage,setStage]=useState("Pending Quote");
  const[priority,setPriority]=useState("Normal");
  const[budget,setBudget]=useState("");
  const[dueDate,setDueDate]=useState("");
  const[thumbnail,setThumbnail]=useState("🪑");
  const[piecesText,setPiecesText]=useState("");
  const[saving,setSaving]=useState(false);

  // Compute effective designer ID — use state, or first available designer
  const effectiveDesignerId = designerId || (designers.length > 0 ? designers[0].id : "");

  // Sync state when designers load
  useEffect(()=>{
    if(!designerId && designers.length > 0) setDesignerId(designers[0].id);
  },[designers, designerId]);

  const save = async () => {
    if(!name.trim()){alert("Project name is required.");return;}
    if(!effectiveDesignerId){alert("No designer accounts found. Please ensure a designer has signed up first.");return;}
    setSaving(true);
    await onSave({
      name:name.trim(),
      designer_id:effectiveDesignerId,
      status,stage,priority,
      budget:budget||"TBD",
      due_date:dueDate||"TBD",
      thumbnail,
      pieces:piecesText.split("\n").map(s=>s.trim()).filter(Boolean),
      progress:0
    });
    setSaving(false);
  };

  return(
    <Modal onClose={onClose} maxWidth={640}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
        <h2 style={{fontFamily:font.display,fontSize:24,fontWeight:600,color:T.cream,fontStyle:"italic"}}>New Project</h2>
        <span onClick={onClose} style={{cursor:"pointer",color:T.textMuted}}><I name="x" size={18}/></span>
      </div>

      <Field label="Project Name *" value={name} onChange={setName} placeholder="Smith Residence — Living Room"/>
      <Field label="Assign to Designer *" value={effectiveDesignerId} onChange={setDesignerId} select options={designers.map(d=>({value:d.id,label:`${d.full_name||d.email} — ${d.firm||""}`}))}/>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
        <Field label="Status" value={status} onChange={setStatus} select options={PROJECT_STATUSES}/>
        <Field label="Priority" value={priority} onChange={setPriority} select options={PRIORITIES}/>
        <Field label="Emoji" value={thumbnail} onChange={setThumbnail} placeholder="🪑"/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <Field label="Budget" value={budget} onChange={setBudget} placeholder="$24,500"/>
        <Field label="Due Date" value={dueDate} onChange={setDueDate} placeholder="2025-06-01"/>
      </div>
      <Field label="Stage" value={stage} onChange={setStage} select options={PROJECT_STAGES}/>
      <Field label="Pieces (one per line)" value={piecesText} onChange={setPiecesText} placeholder={"Custom dining table\nBuilt-in shelving\nMedia console"} textarea/>

      <div style={{display:"flex",gap:10,marginTop:8}}>
        <Btn primary icon="check" onClick={save} disabled={saving}>{saving?"Saving...":"Create Project"}</Btn>
        <Btn ghost onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

function ProjectDetailModal({project,onClose,onRefresh}) {
  const[editMode,setEditMode]=useState(false);
  const[fileUploadMsg,setFileUploadMsg]=useState("");
  const[status,setStatus]=useState(project.status);
  const[stage,setStage]=useState(project.stage||"");
  const[priority,setPriority]=useState(project.priority);
  const[budget,setBudget]=useState(project.budget||"");
  const[dueDate,setDueDate]=useState(project.due_date||"");
  const[progress,setProgress]=useState(project.progress||0);
  const[saving,setSaving]=useState(false);
  const fileRef=useRef();

  const save = async () => {
    setSaving(true);
    await supabase.from("projects").update({status,stage,priority,budget,due_date:dueDate,progress:parseInt(progress)}).eq("id",project.id);
    await onRefresh();
    setSaving(false);
    setEditMode(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if(!file) return;
    const path = `projects/${project.id}/${Date.now()}_${file.name}`;
    const{error:uploadError}=await supabase.storage.from("project-files").upload(path,file);
    if(uploadError){alert("Upload failed: "+uploadError.message);return;}
    await supabase.from("project_files").insert({project_id:project.id,name:file.name,size:`${(file.size/1024/1024).toFixed(1)} MB`,file_type:file.name.split(".").pop(),storage_path:path});
    await onRefresh();
    setFileUploadMsg("File uploaded successfully!");
    setTimeout(()=>setFileUploadMsg(""),3000);
  };

  return(
    <Modal onClose={onClose} maxWidth={680}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:20}}>
        <div>
          <div style={{fontSize:28,marginBottom:6}}>{project.thumbnail||"🪑"}</div>
          <h2 style={{fontFamily:font.display,fontSize:22,fontWeight:600,color:T.cream,fontStyle:"italic"}}>{project.name}</h2>
          <div style={{fontSize:12,color:T.textMuted,marginTop:4}}>{project.profiles?.full_name} · {project.profiles?.firm}</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn small icon="edit" onClick={()=>setEditMode(!editMode)}>{editMode?"Cancel Edit":"Edit"}</Btn>
          <span onClick={onClose} style={{cursor:"pointer",color:T.textMuted,marginLeft:4}}><I name="x" size={18}/></span>
        </div>
      </div>

      {editMode?(
        <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Status" value={status} onChange={setStatus} select options={PROJECT_STATUSES}/>
            <Field label="Priority" value={priority} onChange={setPriority} select options={PRIORITIES}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Budget" value={budget} onChange={setBudget} placeholder="$24,500"/>
            <Field label="Due Date" value={dueDate} onChange={setDueDate} placeholder="2025-06-01"/>
          </div>
          <Field label="Stage" value={stage} onChange={setStage} select options={PROJECT_STAGES}/>
          <Field label="Progress %" value={progress} onChange={setProgress} placeholder="0-100" type="number"/>
          <Btn primary icon="check" onClick={save} disabled={saving}>{saving?"Saving...":"Save Changes"}</Btn>
        </>
      ):(
        <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:16}}>
            {[{l:"Status",v:project.status},{l:"Priority",v:project.priority},{l:"Budget",v:project.budget||"TBD"},{l:"Due Date",v:project.due_date||"TBD"},{l:"Stage",v:project.stage||"—"},{l:"Progress",v:`${project.progress||0}%`}].map(r=>(
              <div key={r.l} style={{background:T.bg3,padding:"12px 14px",borderRadius:3}}>
                <div style={{fontSize:9,color:T.textDim,textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>{r.l}</div>
                <div style={{fontSize:13,fontWeight:600,color:T.cream}}>{r.v}</div>
              </div>
            ))}
          </div>

          {(project.pieces||[]).length>0&&(
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:600,color:T.textMuted,letterSpacing:".1em",textTransform:"uppercase",marginBottom:8}}>Pieces</div>
              {project.pieces.map((piece,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:`1px solid ${T.border}`,fontSize:13,color:T.text}}>
                  <div style={{width:5,height:5,borderRadius:"50%",background:T.gold,flexShrink:0}}/>{piece}
                </div>
              ))}
            </div>
          )}

          {(project.approvals||[]).length>0&&(
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:600,color:T.textMuted,letterSpacing:".1em",textTransform:"uppercase",marginBottom:8}}>Approvals</div>
              {project.approvals.map(a=>(
                <div key={a.id} style={{padding:"10px 14px",background:T.bg3,borderRadius:3,marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:T.cream}}>{a.type}</div>
                    <div style={{fontSize:11,color:T.textMuted}}>{a.description}</div>
                  </div>
                  <Badge color={a.status==="pending"?T.danger:a.status==="approved"?T.success:T.textMuted}>{a.status}</Badge>
                </div>
              ))}
            </div>
          )}

          <Divider/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:10,fontWeight:600,color:T.textMuted,letterSpacing:".1em",textTransform:"uppercase"}}>Files ({(project.project_files||[]).length})</div>
            <Btn small icon="upload" onClick={()=>fileRef.current?.click()}>Upload File</Btn>
            <input ref={fileRef} type="file" onChange={handleFileUpload} style={{display:"none"}}/>
          </div>
          {fileUploadMsg&&<div style={{fontSize:12,color:T.success,padding:"8px 12px",background:`${T.success}10`,borderRadius:3,marginBottom:8}}>{fileUploadMsg}</div>}
          {(project.project_files||[]).map((f,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
              <I name="file" size={14} color={T.gold}/>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:T.text}}>{f.name}</div>
                <div style={{fontSize:10,color:T.textDim}}>{f.size||"—"} · {f.created_at?.split("T")[0]}</div>
              </div>
              <div style={{display:"flex",gap:6}}>
                <span title="View" onClick={async()=>{if(f.storage_path){const{data}=await supabase.storage.from("project-files").createSignedUrl(f.storage_path,60);if(data?.signedUrl)window.open(data.signedUrl,"_blank");}}} style={{cursor:"pointer"}}><I name="eye" size={13} color={T.textDim}/></span>
                <span title="Download" onClick={async()=>{if(f.storage_path){const{data}=await supabase.storage.from("project-files").createSignedUrl(f.storage_path,60);if(data?.signedUrl){const a=document.createElement("a");a.href=data.signedUrl;a.download=f.name;a.click();}}}} style={{cursor:"pointer"}}><I name="download" size={13} color={T.textDim}/></span>
              </div>
            </div>
          ))}
        </>
      )}
    </Modal>
  );
}

function SendMessageModal({project,onClose,onSend}) {
  const[text,setText]=useState("");
  const[sending,setSending]=useState(false);
  const send = async () => {
    if(!text.trim()) return;
    setSending(true);
    await onSend(text);
    setSending(false);
  };
  return(
    <Modal onClose={onClose}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <h2 style={{fontFamily:font.display,fontSize:22,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Send Message</h2>
        <span onClick={onClose} style={{cursor:"pointer",color:T.textMuted}}><I name="x" size={18}/></span>
      </div>
      <div style={{padding:"10px 14px",background:T.bg3,borderRadius:3,marginBottom:16,fontSize:12,color:T.textMuted}}>
        To: <strong style={{color:T.cream}}>{project.profiles?.full_name}</strong> — {project.name}
      </div>
      <Field label="Message" value={text} onChange={setText} placeholder="Type your message to the designer..." textarea/>
      <div style={{display:"flex",gap:10}}>
        <Btn primary icon="send" onClick={send} disabled={sending||!text.trim()}>{sending?"Sending...":"Send Message"}</Btn>
        <Btn ghost onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

function RequestApprovalModal({project,onClose,onSave}) {
  const[type,setType]=useState("");
  const[description,setDescription]=useState("");
  const[optionsText,setOptionsText]=useState("");
  const[saving,setSaving]=useState(false);
  const save = async () => {
    if(!type||!description){alert("Type and description required.");return;}
    setSaving(true);
    const options=optionsText.split("\n").map(s=>s.trim()).filter(Boolean);
    await onSave({type,description,options:options.length>0?options:null});
    setSaving(false);
  };
  return(
    <Modal onClose={onClose}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <h2 style={{fontFamily:font.display,fontSize:22,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Request Approval</h2>
        <span onClick={onClose} style={{cursor:"pointer",color:T.textMuted}}><I name="x" size={18}/></span>
      </div>
      <div style={{padding:"10px 14px",background:T.bg3,borderRadius:3,marginBottom:16,fontSize:12,color:T.textMuted}}>
        Project: <strong style={{color:T.cream}}>{project.name}</strong>
      </div>
      <Field label="Approval Type" value={type} onChange={setType} placeholder="e.g., Finish Selection, Design Drawings"/>
      <Field label="Description" value={description} onChange={setDescription} placeholder="Describe what the designer needs to approve..." textarea/>
      <Field label="Options (one per line, optional)" value={optionsText} onChange={setOptionsText} placeholder={"Ebony Stain\nNatural Oil Finish"} textarea/>
      <div style={{display:"flex",gap:10}}>
        <Btn primary icon="check" onClick={save} disabled={saving}>{saving?"Saving...":"Send to Designer"}</Btn>
        <Btn ghost onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

function AddUpdateModal({project,onClose,onSave}) {
  const[text,setText]=useState("");
  const[progress,setProgress]=useState(project.progress||0);
  const[updateProgress,setUpdateProgress]=useState(false);
  const[saving,setSaving]=useState(false);
  const save = async () => {
    if(!text.trim()){alert("Update text required.");return;}
    setSaving(true);
    await onSave(text,updateProgress?parseInt(progress):null);
    setSaving(false);
  };
  return(
    <Modal onClose={onClose}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <h2 style={{fontFamily:font.display,fontSize:22,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Add Project Update</h2>
        <span onClick={onClose} style={{cursor:"pointer",color:T.textMuted}}><I name="x" size={18}/></span>
      </div>
      <div style={{padding:"10px 14px",background:T.bg3,borderRadius:3,marginBottom:16,fontSize:12,color:T.textMuted}}>
        Project: <strong style={{color:T.cream}}>{project.name}</strong>
      </div>
      <Field label="Update" value={text} onChange={setText} placeholder="Describe the progress update..." textarea/>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,cursor:"pointer"}} onClick={()=>setUpdateProgress(!updateProgress)}>
        <div style={{width:18,height:18,borderRadius:2,border:`1px solid ${updateProgress?T.gold:T.border}`,background:updateProgress?T.gold:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
          {updateProgress&&<I name="check" size={12} color={T.bg}/>}
        </div>
        <span style={{fontSize:12,color:T.textMuted}}>Also update progress percentage</span>
      </div>
      {updateProgress&&<Field label="Progress %" value={progress} onChange={setProgress} placeholder="0-100" type="number"/>}
      <div style={{display:"flex",gap:10}}>
        <Btn primary icon="check" onClick={save} disabled={saving}>{saving?"Saving...":"Post Update"}</Btn>
        <Btn ghost onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}


// ═══════════════════════════════════════
// ADMIN MESSAGES
// ═══════════════════════════════════════
function AdminMessages() {
  const[projects,setProjects]=useState([]);
  const[activeProjectId,setActiveProjectId]=useState(null);
  const[messages,setMessages]=useState([]);
  const[newMessage,setNewMessage]=useState("");
  const[sending,setSending]=useState(false);
  const[loading,setLoading]=useState(true);
  const bottomRef=useRef(null);

  // Load all projects with their last message
  useEffect(()=>{
    supabase.from("projects").select("*,profiles(full_name,firm,email)").order("updated_at",{ascending:false})
      .then(({data})=>{setProjects(data||[]);if(data?.length)setActiveProjectId(data[0].id);setLoading(false);});
  },[]);

  // Load messages for active project
  useEffect(()=>{
    if(!activeProjectId)return;
    supabase.from("messages").select("*").eq("project_id",activeProjectId).order("created_at",{ascending:true})
      .then(({data})=>setMessages(data||[]));
    // Mark designer messages as read
    supabase.from("messages").update({read_by_admin:true}).eq("project_id",activeProjectId).eq("is_espion",false).then(()=>{});
  },[activeProjectId]);

  // Realtime
  useEffect(()=>{
    if(!activeProjectId)return;
    const channel=supabase.channel(`admin_msgs_${activeProjectId}`)
      .on("postgres_changes",{event:"INSERT",schema:"public",table:"messages",filter:`project_id=eq.${activeProjectId}`},(payload)=>{
        setMessages(prev=>[...prev,payload.new]);
      }).subscribe();
    return()=>supabase.removeChannel(channel);
  },[activeProjectId]);

  useEffect(()=>{if(messages.length>0)setTimeout(()=>bottomRef.current?.scrollIntoView({behavior:"smooth"}),100);},[activeProjectId,messages.length]);

  const activeProject=projects.find(p=>p.id===activeProjectId);

  const sendMessage=async()=>{
    if(!newMessage.trim()||sending||!activeProjectId)return;
    const text=newMessage;
    setNewMessage("");setSending(true);
    const{error}=await supabase.from("messages").insert({
      project_id:activeProjectId,
      sender_name:"Espion Team",sender_initial:"ET",is_espion:true,text
    });
    if(!error){
      // Send email to designer
      const profile=activeProject?.profiles;
      if(profile?.email){
        await sendEmail("new_message",profile.email,{
          projectName:activeProject.name,messageText:text,senderName:"Espion Team"
        });
      }
    }
    setSending(false);
  };

  // Get last message per project for sidebar preview
  const[lastMessages,setLastMessages]=useState({});
  useEffect(()=>{
    if(!projects.length)return;
    Promise.all(projects.map(p=>
      supabase.from("messages").select("text,created_at,is_espion")
        .eq("project_id",p.id).order("created_at",{ascending:false}).limit(1)
        .then(({data})=>({id:p.id,msg:data?.[0]||null}))
        .catch(()=>({id:p.id,msg:null}))
    )).then(results=>{
      const map={};
      results.forEach(r=>{if(r.msg)map[r.id]=r.msg;});
      setLastMessages(map);
    });
  },[projects]);

  return(
    <div>
      <div style={{marginBottom:24}}>
        <h2 style={{fontFamily:font.display,fontSize:32,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Messages</h2>
        <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>Two-way conversations with designers, organized by project.</p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"300px 1fr",gap:0,background:T.bg2,border:`1px solid ${T.border}`,borderRadius:4,height:580}}>
        {/* Project list — fixed, scrolls independently */}
        <div style={{borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
          <div style={{padding:"14px 20px",borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
            <span style={{fontSize:11,fontWeight:600,color:T.textDim,letterSpacing:".1em",textTransform:"uppercase"}}>Projects</span>
          </div>
          <div style={{flex:1,overflowY:"auto",overflowX:"hidden"}}>
            {loading&&<div style={{padding:20,color:T.textMuted,fontSize:13}}>Loading...</div>}
            {projects.map(p=>{
              const isActive=activeProjectId===p.id;
              const last=lastMessages[p.id];
              return(
                <div key={p.id} onClick={()=>setActiveProjectId(p.id)} style={{padding:"14px 20px",cursor:"pointer",background:isActive?`${T.gold}10`:"transparent",borderRight:isActive?`2px solid ${T.gold}`:"2px solid transparent",transition:"all .2s"}} onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=T.bg3}} onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="transparent"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:4}}>
                    <span style={{fontSize:12,fontWeight:600,color:isActive?T.gold:T.cream,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{p.name}</span>
                  </div>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:3}}>{p.profiles?.full_name||"—"} · {p.profiles?.firm||""}</div>
                  {last&&<div style={{fontSize:11,color:T.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{last.is_espion?"You: ":""}{last.text}</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat area — scrolls independently */}
        <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
          {!activeProject?(
            <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:T.textMuted,fontSize:13}}>
              Select a project to view the conversation.
            </div>
          ):(
            <>
              <div style={{padding:"14px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:T.cream}}>{activeProject.name}</div>
                  <div style={{fontSize:11,color:T.textMuted}}>{activeProject.profiles?.full_name} · {activeProject.profiles?.firm} · {activeProject.profiles?.email}</div>
                </div>
              </div>

              <div style={{flex:1,overflow:"auto",padding:"20px 24px"}}>
                {messages.length===0&&(
                  <div style={{textAlign:"center",padding:40,color:T.textMuted}}>
                    <I name="message" size={32} color={T.textDim}/>
                    <div style={{marginTop:12,fontSize:13}}>No messages yet on this project.</div>
                  </div>
                )}
                {messages.map(m=>{
                  const isEspion=m.is_espion;
                  return(
                    <div key={m.id} style={{display:"flex",gap:10,marginBottom:16,justifyContent:isEspion?"flex-end":"flex-start"}}>
                      {!isEspion&&(
                        <div style={{width:32,height:32,borderRadius:"50%",background:`linear-gradient(135deg,${T.gold},${T.goldDark})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:T.bg,flexShrink:0}}>
                          {(m.sender_initial||"D")}
                        </div>
                      )}
                      <div style={{maxWidth:"70%"}}>
                        {!isEspion&&<div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>{m.sender_name} · {new Date(m.created_at).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}</div>}
                        <div style={{padding:"10px 14px",borderRadius:isEspion?"12px 12px 2px 12px":"12px 12px 12px 2px",background:isEspion?`linear-gradient(135deg,${T.gold},${T.goldDark})`:T.bg3,color:isEspion?T.bg:T.text,fontSize:13,lineHeight:1.6}}>
                          {m.text}
                        </div>
                        {isEspion&&<div style={{fontSize:11,color:T.textMuted,marginTop:4,textAlign:"right"}}>{new Date(m.created_at).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}</div>}
                      </div>
                      {isEspion&&(
                        <div style={{width:32,height:32,borderRadius:"50%",background:T.bg4,border:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:T.gold,flexShrink:0}}>ET</div>
                      )}
                    </div>
                  );
                })}
                <div ref={bottomRef}/>
              </div>

              <div style={{padding:"16px 24px",borderTop:`1px solid ${T.border}`,display:"flex",gap:10}}>
                <input
                  value={newMessage}
                  onChange={e=>setNewMessage(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMessage()}
                  placeholder="Type a message to the designer..."
                  style={{flex:1,padding:"10px 14px",background:T.bg3,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,outline:"none"}}
                />
                <Btn primary icon="send" small onClick={sendMessage} disabled={!newMessage.trim()||sending}>{sending?"...":"Send"}</Btn>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// INVOICES
// ═══════════════════════════════════════
function Invoices() {
  const[invoices,setInvoices]=useState([]);
  const[designers,setDesigners]=useState([]);
  const[projects,setProjects]=useState([]);
  const[loading,setLoading]=useState(true);
  const[showNew,setShowNew]=useState(false);

  const fetch = async () => {
    const[invRes,desRes,projRes]=await Promise.all([
      supabase.from("invoices").select("*,profiles(full_name,firm)").order("created_at",{ascending:false}),
      supabase.from("profiles").select("id,full_name,firm"),
      supabase.from("projects").select("id,name,designer_id"),
    ]);
    setInvoices(invRes.data||[]);
    setDesigners(desRes.data||[]);
    setProjects(projRes.data||[]);
    setLoading(false);
  };

  useEffect(()=>{fetch();},[]);

  const sc={Outstanding:T.danger,Paid:T.success,Upcoming:T.blue,Cancelled:T.textMuted};

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28}}>
        <div>
          <h2 style={{fontFamily:font.display,fontSize:32,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Invoices</h2>
          <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>{invoices.filter(i=>i.status==="Outstanding").length} outstanding</p>
        </div>
        <Btn primary icon="plus" onClick={()=>setShowNew(true)}>New Invoice</Btn>
      </div>

      <Card style={{padding:0,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 2fr 1.5fr 1fr 1fr 80px",padding:"10px 24px",borderBottom:`1px solid ${T.border}`,fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:T.textDim}}>
          <span>Invoice</span><span>Project</span><span>Designer</span><span>Amount</span><span>Status</span><span></span>
        </div>
        {loading&&<div style={{padding:32,textAlign:"center",color:T.textMuted}}>Loading...</div>}
        {invoices.map((inv,i)=>(
          <div key={inv.id} style={{display:"grid",gridTemplateColumns:"1fr 2fr 1.5fr 1fr 1fr 80px",padding:"16px 24px",borderBottom:i<invoices.length-1?`1px solid ${T.border}`:"none",alignItems:"center"}}>
            <div style={{fontSize:12,fontWeight:600,color:T.cream}}>{inv.id}</div>
            <div>
              <div style={{fontSize:12,color:T.text}}>{inv.project_name}</div>
              <div style={{fontSize:10,color:T.textDim}}>{inv.invoice_type}</div>
            </div>
            <div style={{fontSize:12,color:T.textMuted}}>{inv.profiles?.full_name||"—"}</div>
            <div style={{fontFamily:font.display,fontSize:16,fontWeight:600,color:T.gold,fontStyle:"italic"}}>{inv.amount}</div>
            <Badge color={sc[inv.status]||T.textMuted}>{inv.status}</Badge>
            <select value={inv.status} onChange={async e=>{await supabase.from("invoices").update({status:e.target.value}).eq("id",inv.id);await fetch();}} style={{background:T.bg3,border:`1px solid ${T.border}`,borderRadius:2,color:T.text,fontSize:11,padding:"4px 8px",cursor:"pointer"}}>
              {INVOICE_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ))}
      </Card>

      {showNew&&<NewInvoiceModal designers={designers} projects={projects} onClose={()=>setShowNew(false)} onSave={async(data)=>{await supabase.from("invoices").insert(data);await fetch();setShowNew(false);}}/>}
    </div>
  );
}

function NewInvoiceModal({designers,projects,onClose,onSave}) {
  const[designerId,setDesignerId]=useState(designers[0]?.id||"");
  useEffect(()=>{if(!designerId&&designers.length>0)setDesignerId(designers[0].id);},[designers]);
  const[projectId,setProjectId]=useState("");
  const[amount,setAmount]=useState("");
  const[invoiceType,setInvoiceType]=useState("Deposit (33%)");
  const[status,setStatus]=useState("Outstanding");
  const[invoiceDate,setInvoiceDate]=useState(new Date().toISOString().split("T")[0]);
  const[dueDate,setDueDate]=useState("");
  const[squareUrl,setSquareUrl]=useState("");
  const[saving,setSaving]=useState(false);

  const designerProjects=projects.filter(p=>p.designer_id===designerId);
  const selectedProject=projects.find(p=>p.id===projectId);

  const save = async () => {
    if(!amount||!designerId){alert("Amount and designer required.");return;}
    setSaving(true);
    const id=`INV-${Date.now()}`;
    const invoiceData={
      id,designer_id:designerId,project_id:projectId||null,
      project_name:selectedProject?.name||"General",
      amount,invoice_type:invoiceType,status,
      invoice_date:invoiceDate,due_date:dueDate||null,square_url:squareUrl||null,
    };
    await onSave(invoiceData);
    // Send email notification for outstanding invoices
    if(status==="Outstanding"){
      const{data:profile}=await supabase.from("profiles").select("email").eq("id",designerId).single();
      if(profile?.email){
        await sendEmail("invoice",profile.email,{
          invoiceId:id,
          projectName:selectedProject?.name||"General",
          amount,
          invoiceType,
          dueDate:dueDate||"Upon receipt",
          squareUrl:squareUrl||null,
        });
      }
    }
    setSaving(false);
  };

  return(
    <Modal onClose={onClose}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
        <h2 style={{fontFamily:font.display,fontSize:22,fontWeight:600,color:T.cream,fontStyle:"italic"}}>New Invoice</h2>
        <span onClick={onClose} style={{cursor:"pointer",color:T.textMuted}}><I name="x" size={18}/></span>
      </div>
      <Field label="Designer" value={designerId} onChange={v=>{setDesignerId(v);setProjectId("");}} select options={designers.map(d=>({value:d.id,label:`${d.full_name||d.email} — ${d.firm||""}`}))}/>
      <Field label="Project (optional)" value={projectId} onChange={setProjectId} select options={[{value:"",label:"— General (no project) —"},...designerProjects.map(p=>({value:p.id,label:p.name}))]}/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <Field label="Amount" value={amount} onChange={setAmount} placeholder="$8,166.67"/>
        <Field label="Status" value={status} onChange={setStatus} select options={INVOICE_STATUSES}/>
      </div>
      <Field label="Invoice Type" value={invoiceType} onChange={setInvoiceType} placeholder="e.g., Deposit (33%), Progress Payment 2 of 3"/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <Field label="Invoice Date" value={invoiceDate} onChange={setInvoiceDate} type="date"/>
        <Field label="Due Date" value={dueDate} onChange={setDueDate} type="date"/>
      </div>
      <Field label="Square Payment URL (optional)" value={squareUrl} onChange={setSquareUrl} placeholder="https://square.link/..."/>
      <div style={{display:"flex",gap:10}}>
        <Btn primary icon="check" onClick={save} disabled={saving}>{saving?"Saving...":"Create Invoice"}</Btn>
        <Btn ghost onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════
// RESOURCES
// ═══════════════════════════════════════
function Resources() {
  const[resources,setResources]=useState([]);
  const[loading,setLoading]=useState(true);
  const[showNew,setShowNew]=useState(false);

  const fetch = async () => {
    const{data}=await supabase.from("resources").select("*").order("category");
    setResources(data||[]);
    setLoading(false);
  };
  useEffect(()=>{fetch();},[]);

  const toggle = async (r) => {
    await supabase.from("resources").update({published:!r.published}).eq("id",r.id);
    await fetch();
  };

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28}}>
        <div>
          <h2 style={{fontFamily:font.display,fontSize:32,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Resource Library</h2>
          <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>{resources.length} resources</p>
        </div>
        <Btn primary icon="plus" onClick={()=>setShowNew(true)}>Add Resource</Btn>
      </div>

      <Card style={{padding:0,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 2fr 1fr 1fr 80px",padding:"10px 24px",borderBottom:`1px solid ${T.border}`,fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:T.textDim}}>
          <span>Category</span><span>Title</span><span>Type</span><span>Status</span><span></span>
        </div>
        {loading&&<div style={{padding:32,textAlign:"center",color:T.textMuted}}>Loading...</div>}
        {resources.map((r,i)=>(
          <div key={r.id} style={{display:"grid",gridTemplateColumns:"1fr 2fr 1fr 1fr 80px",padding:"14px 24px",borderBottom:i<resources.length-1?`1px solid ${T.border}`:"none",alignItems:"center"}}>
            <Badge color={T.blue}>{r.category}</Badge>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:T.cream}}>{r.title}</div>
              <div style={{fontSize:11,color:T.textDim}}>{r.file_size||"—"}</div>
            </div>
            <div style={{fontSize:12,color:T.textMuted,textTransform:"uppercase"}}>{r.file_type||"—"}</div>
            <Badge color={r.published?T.success:T.textMuted}>{r.published?"Published":"Hidden"}</Badge>
            <Btn small onClick={()=>toggle(r)}>{r.published?"Hide":"Show"}</Btn>
          </div>
        ))}
      </Card>

      {showNew&&<NewResourceModal onClose={()=>setShowNew(false)} onSave={async(data)=>{await supabase.from("resources").insert(data);await fetch();setShowNew(false);}}/>}
    </div>
  );
}

function NewResourceModal({onClose,onSave}) {
  const[category,setCategory]=useState("Catalogs");
  const[title,setTitle]=useState("");
  const[description,setDescription]=useState("");
  const[fileSize,setFileSize]=useState("");
  const[fileType,setFileType]=useState("pdf");
  const[downloadUrl,setDownloadUrl]=useState("");
  const[saving,setSaving]=useState(false);
  const cats=["Catalogs","Materials","Guidelines","Inspiration","Legal"];

  const save = async () => {
    if(!title){alert("Title required.");return;}
    setSaving(true);
    await onSave({category,title,description,file_size:fileSize,file_type:fileType,download_url:downloadUrl||null,published:true});
    setSaving(false);
  };

  return(
    <Modal onClose={onClose}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
        <h2 style={{fontFamily:font.display,fontSize:22,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Add Resource</h2>
        <span onClick={onClose} style={{cursor:"pointer",color:T.textMuted}}><I name="x" size={18}/></span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <Field label="Category" value={category} onChange={setCategory} select options={cats}/>
        <Field label="File Type" value={fileType} onChange={setFileType} placeholder="pdf"/>
      </div>
      <Field label="Title" value={title} onChange={setTitle} placeholder="2025 Collection Lookbook"/>
      <Field label="Description" value={description} onChange={setDescription} placeholder="Brief description..." textarea/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <Field label="File Size" value={fileSize} onChange={setFileSize} placeholder="18.4 MB"/>
        <Field label="Download URL (optional)" value={downloadUrl} onChange={setDownloadUrl} placeholder="https://..."/>
      </div>
      <div style={{display:"flex",gap:10}}>
        <Btn primary icon="check" onClick={save} disabled={saving}>{saving?"Saving...":"Add Resource"}</Btn>
        <Btn ghost onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════
export default function EspionAdmin() {
  const[user,setUser]=useState(null);
  const[loading,setLoading]=useState(true);
  const[section,setSection]=useState(()=>{
    try{return localStorage.getItem("espion_admin_section")||"dashboard";}catch{return "dashboard";}
  });
  const[stats,setStats]=useState({designers:0,activeProjects:0,pendingApprovals:0,outstandingInvoices:0,recentProjects:[],recentDesigners:[]});
  const[unreadMessages,setUnreadMessages]=useState(0);

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      if(session?.user)setUser(session.user);
      setLoading(false);
    }).catch(()=>setLoading(false));
    const{data:{subscription}}=supabase.auth.onAuthStateChange((_,session)=>{
      setUser(session?.user||null);
    });
    return()=>subscription.unsubscribe();
  },[]);

  useEffect(()=>{
    if(!user) return;
    const fetchStats = async () => {
      const[desRes,projRes,approvalRes,invRes]=await Promise.all([
        supabase.from("profiles").select("*").order("created_at",{ascending:false}),
        supabase.from("projects").select("*,profiles(full_name,firm)").order("created_at",{ascending:false}),
        supabase.from("approvals").select("*").eq("status","pending"),
        supabase.from("invoices").select("*").eq("status","Outstanding"),
      ]);
      setStats({
        designers:(desRes.data||[]).length,
        activeProjects:(projRes.data||[]).filter(p=>p.status!=="Completed").length,
        pendingApprovals:(approvalRes.data||[]).length,
        outstandingInvoices:(invRes.data||[]).length,
        recentProjects:(projRes.data||[]).slice(0,5),
        recentDesigners:(desRes.data||[]).slice(0,5),
      });
    };
    fetchStats();
  },[user,section]);

  useEffect(()=>{
    if(!user)return;
    const fetchUnread=()=>supabase.from("messages").select("id",{count:"exact",head:true}).eq("read_by_admin",false).eq("is_espion",false).then(({count})=>setUnreadMessages(count||0));
    // Delay slightly when leaving messages section so mark-as-read has time to write
    if(section!=="messages"){
      fetchUnread();
    } else {
      setTimeout(fetchUnread, 800);
    }
  },[user,section]);

  useEffect(()=>{
    try{localStorage.setItem("espion_admin_section",section);}catch{}
  },[section]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    try{localStorage.removeItem("espion_admin_section");}catch{}
  };

  const nav=[
    {id:"dashboard",label:"Dashboard",icon:"home"},
    {id:"designers",label:"Designers",icon:"users"},
    {id:"projects",label:"Projects",icon:"folder"},
    {id:"messages",label:"Messages",icon:"message"},
    {id:"invoices",label:"Invoices",icon:"dollar"},
    {id:"resources",label:"Resources",icon:"book"},
  ];

  if(loading) return(
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <style>{css}</style>
      <div style={{textAlign:"center"}}>
        <EspionMark size={48} style={{margin:"0 auto"}}/>
        <div style={{fontSize:11,color:T.textDim,marginTop:6,letterSpacing:".12em",textTransform:"uppercase"}}>Loading admin...</div>
      </div>
    </div>
  );

  if(!user) return(
    <>
      <style>{css}</style>
      <AdminAuth onAuth={u=>setUser(u)}/>
    </>
  );

  const pendingCount=stats.pendingApprovals;

  return(
    <div style={{display:"flex",minHeight:"100vh",background:T.bg}}>
      <style>{css}</style>

      {/* Sidebar */}
      <aside style={{width:220,minWidth:220,background:T.bg2,borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column",position:"sticky",top:0,height:"100vh"}}>
        <div style={{padding:"20px 20px",borderBottom:`1px solid ${T.border}`}}>
          <EspionMark size={28} showText={false}/>
          <div style={{marginTop:4,fontSize:8,color:T.textDim,letterSpacing:".14em",textTransform:"uppercase",fontFamily:font.body}}>Admin Console</div>
        </div>

        <nav style={{flex:1,padding:"12px 8px"}}>
          {nav.map(n=>{
            const active=section===n.id;
            const badge=n.id==="projects"&&pendingCount>0?pendingCount:n.id==="messages"&&unreadMessages>0?unreadMessages:null;
            return(
              <div key={n.id} onClick={()=>setSection(n.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",marginBottom:2,borderRadius:3,cursor:"pointer",background:active?`${T.gold}12`:"transparent",borderRight:active?`2px solid ${T.gold}`:"2px solid transparent",transition:"all .2s"}} onMouseEnter={e=>{if(!active)e.currentTarget.style.background=`${T.gold}06`}} onMouseLeave={e=>{if(!active)e.currentTarget.style.background="transparent"}}>
                <I name={n.icon} size={16} color={active?T.gold:T.textDim}/>
                <span style={{fontSize:12,fontWeight:active?600:400,color:active?T.cream:T.textMuted,flex:1}}>{n.label}</span>
                {badge&&<div style={{width:18,height:18,borderRadius:"50%",background:T.danger,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:"#fff"}}>{badge}</div>}
              </div>
            );
          })}
        </nav>

        <div style={{padding:"12px 8px",borderTop:`1px solid ${T.border}`}}>
          <div style={{padding:"10px 12px",marginBottom:8,background:T.bg3,borderRadius:3}}>
            <div style={{fontSize:12,fontWeight:600,color:T.cream}}>Espion Team</div>
            <div style={{fontSize:10,color:T.textDim,marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.email}</div>
          </div>
          <div onClick={handleSignOut} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:3,cursor:"pointer",color:T.danger}} onMouseEnter={e=>e.currentTarget.style.background=`${T.danger}10`} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <I name="logout" size={14} color={T.danger}/>
            <span style={{fontSize:12,fontWeight:600}}>Sign Out</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main style={{flex:1,overflow:"auto",background:T.bg}}>
        <header style={{position:"sticky",top:0,zIndex:9,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 32px",background:`${T.bg}f0`,borderBottom:`1px solid ${T.border}`,backdropFilter:"blur(20px)"}}>
          <h1 style={{fontFamily:font.display,fontSize:18,fontWeight:500,color:T.cream,fontStyle:"italic"}}>{nav.find(n=>n.id===section)?.label}</h1>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <Badge color={T.success}>Admin</Badge>
            <span style={{fontSize:11,color:T.textDim}}>Espion Furniture Co.</span>
          </div>
        </header>

        <div style={{padding:32}} className="fade">
          {section==="dashboard"&&<AdminDashboard stats={stats} onNavigate={setSection}/>}
          {section==="designers"&&<Designers/>}
          {section==="projects"&&<Projects/>}
          {section==="invoices"&&<Invoices/>}
          {section==="resources"&&<Resources/>}
          {section==="messages"&&<AdminMessages/>}
        </div>
      </main>
    </div>
  );
}
